import React, { useEffect, useState } from "react";
import {
  Card,
  Switch,
  InputNumber,
  Button,
  Upload,
  Slider,
  message,
  Tag,
  Alert,
  Spin,
} from "antd";
import { UploadOutlined, SoundOutlined, DeleteOutlined } from "@ant-design/icons";
import { Header } from "../../../../../components";
import {
  useFetchNotificationSetting,
  useSaveNotificationSetting,
  useUploadNotificationSound,
} from "../hooks/useFetch";
import {
  unlockAudio,
  playNotificationSound,
  clearSoundCache,
} from "../../../../../services/notificationSound";

const MAX_MB = 1;

function NotificationSettingPage() {
  const titlePage = "เสียงแจ้งเตือน";
  const { data, isLoading, refetch } = useFetchNotificationSetting();
  const { mutateAsync: saveSetting, isLoading: isSaving } = useSaveNotificationSetting();
  const { mutateAsync: uploadSound, isLoading: isUploading } = useUploadNotificationSound();

  // แก้ในหน้าจอก่อน กดบันทึกค่อยส่ง — จะได้ปรับหลายค่าแล้วบันทึกทีเดียว
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (data) {
      const d = {};
      data.forEach((s) => { d[s.alertType] = { ...s }; });
      setDraft(d);
    }
  }, [data]);

  const patch = (type, key, value) =>
    setDraft((prev) => ({ ...prev, [type]: { ...prev[type], [key]: value } }));

  const handlePreview = async (s) => {
    // ต้องปลดล็อกจากใน onClick เท่านั้น (autoplay policy ของเบราว์เซอร์)
    const ok = await unlockAudio();
    if (!ok) return message.warning("เบราว์เซอร์นี้เล่นเสียงไม่ได้");
    clearSoundCache(); // เผื่อเพิ่งเปลี่ยนไฟล์ จะได้ไม่เล่นของเก่าค้าง
    playNotificationSound({
      soundUrl: s.soundUrl,
      volume: s.volume,
      urgent: s.alertType === "hold_too_long",
      force: true, // ฟังตัวอย่างได้แม้ผู้ใช้ปิดเสียงไว้
    });
  };

  const handleUpload = async (file, alertType) => {
    if (file.size > MAX_MB * 1024 * 1024) {
      message.error(`ไฟล์ใหญ่เกิน ${MAX_MB} MB (ไฟล์นี้ ${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      return Upload.LIST_IGNORE;
    }
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await uploadSound({ alertType, file: base64 });
      const url = res?.result?.soundUrl;
      if (!url) throw new Error("ไม่ได้รับลิงก์ไฟล์กลับมา");
      patch(alertType, "soundUrl", url);
      clearSoundCache();
      message.success("อัปโหลดเสียงสำเร็จ — อย่าลืมกดบันทึก");
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "อัปโหลดไม่สำเร็จ");
    }
    return Upload.LIST_IGNORE; // จัดการเองทั้งหมด ไม่ให้ antd ยิง request ซ้ำ
  };

  const handleSave = async (alertType) => {
    const s = draft[alertType];
    try {
      await saveSetting({
        alertType,
        enabled: s.enabled,
        thresholdSeconds: s.thresholdSeconds,
        repeatSeconds: s.repeatSeconds,
        soundUrl: s.soundUrl,
        volume: s.volume,
      });
      message.success("บันทึกสำเร็จ");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  if (isLoading) {
    return (
      <div className="md:m-10 flex justify-center py-20">
        <Spin />
      </div>
    );
  }

  return (
    <div className="md:m-10">
      <Header category="App" title={titlePage} />

      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="พนักงานต้องกดปุ่มกระดิ่งบนแถบด้านบนเปิดเสียงเองหนึ่งครั้ง"
        description="เบราว์เซอร์ห้ามเล่นเสียงจนกว่าผู้ใช้จะคลิกอะไรสักอย่างในหน้าก่อน ค่าที่ตั้งตรงนี้จะมีผลกับพนักงานภายใน 5 นาที โดยไม่ต้องรีเฟรชหน้า"
      />

      {(data || []).map((row) => {
        const s = draft[row.alertType] || row;
        return (
          <Card
            key={row.alertType}
            className="mb-4"
            title={
              <div className="flex items-center gap-2">
                <SoundOutlined />
                <span>{row.label}</span>
                {row.isDefault && <Tag color="default">ยังไม่เคยตั้งค่า</Tag>}
                {!s.enabled && <Tag color="red">ปิดอยู่</Tag>}
              </div>
            }
            extra={
              <Switch
                checked={!!s.enabled}
                onChange={(v) => patch(row.alertType, "enabled", v)}
              />
            }
          >
            <p className="text-gray-500 mb-4">{row.description}</p>

            <div className="grid gap-4 lg:grid-cols-2">
              {row.hasThreshold && (
                <>
                  <div>
                    <div className="mb-1 font-medium">ถือเคสนานเกินกี่วินาทีถึงเตือน</div>
                    <InputNumber
                      min={30}
                      max={3600}
                      step={10}
                      value={s.thresholdSeconds}
                      onChange={(v) => patch(row.alertType, "thresholdSeconds", v)}
                      addonAfter="วินาที"
                      style={{ width: 180 }}
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      ต่ำสุด 30 วินาที · 120 = 2 นาที
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 font-medium">เตือนซ้ำทุกกี่วินาที</div>
                    <InputNumber
                      min={5}
                      max={600}
                      step={5}
                      value={s.repeatSeconds}
                      onChange={(v) => patch(row.alertType, "repeatSeconds", v)}
                      addonAfter="วินาที"
                      style={{ width: 180 }}
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      ดังซ้ำจนกว่าจะกด Start · ต่ำสุด 5 วินาที
                    </div>
                  </div>
                </>
              )}

              <div>
                <div className="mb-1 font-medium">ความดัง</div>
                <Slider
                  min={0.1}
                  max={1}
                  step={0.1}
                  value={s.volume}
                  onChange={(v) => patch(row.alertType, "volume", v)}
                  style={{ maxWidth: 260 }}
                />
              </div>

              <div>
                <div className="mb-1 font-medium">ไฟล์เสียง</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Upload
                    accept=".mp3,.wav,.ogg,audio/*"
                    showUploadList={false}
                    beforeUpload={(file) => handleUpload(file, row.alertType)}
                  >
                    <Button icon={<UploadOutlined />} loading={isUploading}>
                      อัปโหลดเสียง
                    </Button>
                  </Upload>

                  <Button icon={<SoundOutlined />} onClick={() => handlePreview(s)}>
                    ฟังตัวอย่าง
                  </Button>

                  {s.soundUrl && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => patch(row.alertType, "soundUrl", null)}
                    >
                      กลับไปใช้เสียงในตัว
                    </Button>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {s.soundUrl ? "ใช้ไฟล์ที่อัปโหลด" : "ใช้เสียงในตัวของระบบ"} · รองรับ mp3 / wav / ogg
                  ไม่เกิน {MAX_MB} MB
                </div>
              </div>
            </div>

            <div className="mt-5">
              <Button
                type="primary"
                loading={isSaving}
                disabled={isSaving}
                onClick={() => handleSave(row.alertType)}
              >
                บันทึก
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export default NotificationSettingPage;
